import React, { useCallback, useEffect, useState } from "react";
import { Collapse } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import { MdKeyboardArrowDown, MdSearch, MdRefresh, MdFileDownload } from "react-icons/md";
import { toast } from "react-toastify";
import {
  Utils,
  fetchWithAuth,
  selectStyles,
  fetchAjaxListData,
  buildCampaignDetailNavigation,
  fetchApplicationAppId,
  bigAmountFormat,
  getAuthUserCurrency,
  getDateRangeForPreset,
  toLocalIsoDate,
  getBrowserTimeZone,
  DATE_RANGE_PRESETS,
  exportListCsv,
  composeEventMetricLabel,
} from "../../utils";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import CampaignPerformanceTable from "./CampaignPerformanceTable";
import DateRangePicker from "../common/DateRangePicker/DateRangePicker";
import "./engage.css";

// Clicks/installs/signups/rates only — event-metric (booking) tiles render separately below,
// grouped into Campaign / Organic / Total rows across every configured metric (see the
// "Event metrics" section in the render, fed by `summary.metrics`).
const SUMMARY_METRICS = [
  { key: "clicks", label: "Clicks", color: "#4c5fd5" },
  { key: "campaign_installs", label: "Campaign Installs", color: "#1b7a3f" },
  { key: "campaign_signups", label: "Campaign Signups", color: "#9a6700" },
  {
    key: "signup_rate",
    label: "Campaign Install → Signup %",
    suffix: "%",
    color: "#8b6b85",
  },
  {
    key: "conversion_rate",
    label: "Click → Install %",
    suffix: "%",
    color: "#6b7785",
  },
  { key: "organic_installs", label: "Organic Installs", color: "#6b9e3f", organicLink: true },
  { key: "organic_signups", label: "Organic Signups", color: "#b8860b", organicLink: true },
  {
    key: "organic_signup_rate",
    label: "Organic Install → Signup %",
    suffix: "%",
    color: "#8b6b85",
    compute: (s) => {
      const inst = s.organic_installs || 0;
      if (!inst) return 0;
      return Number((((s.organic_signups || 0) / inst) * 100).toFixed(2));
    },
  },
  { key: "installs", label: "Total Installs", color: "#15634a" },
  {
    key: "total_signups",
    label: "Total Signups",
    color: "#7a5200",
    compute: (s) => (s.campaign_signups || 0) + (s.organic_signups || 0),
  },
];

// One row per scope (Campaign / Organic / Total) — each row lists every configured event
// metric's Count + Amount tile for that scope, so metrics stay grouped by scope instead of
// scattered among the generic clicks/installs/signups tiles above.
const EVENT_METRIC_SCOPE_ROWS = [
  { key: "campaign", rowLabel: "Campaign", countField: "count_campaign", amountField: "amount_campaign", color: "#0d6e4f" },
  { key: "organic", rowLabel: "Organic", countField: "count_organic", amountField: "amount_organic", color: "#0d6e4f" },
  { key: "total", rowLabel: "Total", countField: "count", amountField: "amount", color: "#0a5c42" },
];

const defaultRange = getDateRangeForPreset("today");

export default function CampaignDashboard() {
  const navigate = useNavigate();
  const { appId: routeAppId } = useParams();
  const [appUuid, setAppUuid] = useState(null);
  const loadApp = useCallback(async () => {
    const uuid = await fetchApplicationAppId(routeAppId);
    if (uuid) setAppUuid(uuid);
    return uuid;
  }, [routeAppId]);

  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [report, setReport] = useState(null);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [datePreset, setDatePreset] = useState(defaultRange.preset || "today");
  const [filterCampaignId, setFilterCampaignId] = useState("");
  const [draftDateFrom, setDraftDateFrom] = useState(defaultRange.dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(defaultRange.dateTo);
  const [draftDatePreset, setDraftDatePreset] = useState(defaultRange.preset || "today");
  const [draftCampaignId, setDraftCampaignId] = useState("");
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);

  const fetchCampaignOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "campaigns",
        q: search,
        id: ids,
        extra: { app_id: appUuid },
        mode: "options",
      }),
    [appUuid]
  );

  const loadReport = useCallback(
    async (uuid) => {
      const app_id = uuid || appUuid;
      if (!app_id) return;
      setLoading(true);
      try {
        const res = await fetchWithAuth(`${Utils.baseurl}get/campaign/report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            app_id,
            date_from: dateFrom,
            date_to: dateTo,
            timezone: getBrowserTimeZone() || undefined,
            campaignId: filterCampaignId || undefined,
          }),
        });
        const json = await res.json();
        if (json.status) setReport(json.data);
      } finally {
        setLoading(false);
      }
    },
    [appUuid, dateFrom, dateTo, filterCampaignId]
  );

  const refreshAll = useCallback(async () => {
    const uuid = (await loadApp()) || appUuid;
    await loadReport(uuid);
  }, [loadApp, loadReport, appUuid]);

  useEffect(() => {
    refreshAll();
  }, [routeAppId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (appUuid) loadReport();
  }, [dateFrom, dateTo, filterCampaignId, appUuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateRangeChange = ({ dateFrom: from, dateTo: to, preset }) => {
    setDraftDateFrom(from || toLocalIsoDate());
    setDraftDateTo(to || toLocalIsoDate());
    if (preset) setDraftDatePreset(preset);
  };

  const onApplyFilters = () => {
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    setDatePreset(draftDatePreset);
    setFilterCampaignId(draftCampaignId);
    setFilterAccordionOpen(Boolean(draftCampaignId));
  };

  const onResetFilters = () => {
    const range = getDateRangeForPreset("today");
    setDraftDateFrom(range.dateFrom);
    setDraftDateTo(range.dateTo);
    setDraftDatePreset(range.preset || "today");
    setDraftCampaignId("");
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setDatePreset(range.preset || "today");
    setFilterCampaignId("");
    setFilterAccordionOpen(false);
  };

  const summary = report?.summary || {};

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("get/campaign/report", {
        app_id: appUuid,
        date_from: dateFrom,
        date_to: dateTo,
        timezone: getBrowserTimeZone() || undefined,
        campaignId: filterCampaignId || undefined,
      }, "campaign-performance.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Campaign</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={refreshAll}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown
              className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
              size={22}
            />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="campaign-filter-bar">
          <div className="campaign-filter-item campaign-filter-item--date-range">
            <DateRangePicker
              dateFrom={draftDateFrom}
              dateTo={draftDateTo}
              preset={draftDatePreset}
              presets={DATE_RANGE_PRESETS}
              allowClear={false}
              onChange={handleDateRangeChange}
            />
          </div>
          <div className="campaign-filter-item campaign-filter-item--link">
            <label>Campaign</label>
            <AsyncSearchSelect
              classNamePrefix="ns-select"
              isClearable
              placeholder="Select campaigns — type to search"
              enabled={Boolean(appUuid)}
              fetchOptions={fetchCampaignOpts}
              value={draftCampaignId || null}
              onChange={(opt) => setDraftCampaignId(opt?.value ?? "")}
              styles={selectStyles}
            />
          </div>
          <div className="campaign-filter-item campaign-filter-item--actions">
            <div className="add_app">
              <button type="button" className="add_app_btn btn_theme_primary" title="Apply filters" onClick={onApplyFilters}>
                <MdSearch />
                <span>Search</span>
              </button>
              <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Reset filters" onClick={onResetFilters}>
                <MdRefresh />
                <span>Reset</span>
              </button>
            </div>
          </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="campaign-summary-row">
          {SUMMARY_METRICS.map(({ key, label, suffix, color, compute, organicLink }) => {
            const raw = compute ? compute(summary) : (summary[key] ?? 0);
            const value = loading
              ? "—"
              : suffix === "%"
                ? `${raw}${suffix}`
                : bigAmountFormat(raw);

            const displayLabel = label;

            if (organicLink && routeAppId) {
              return (
                <div key={key} className="campaign-summary-card">
                  <span className="campaign-summary-label">{displayLabel}</span>
                  <strong className="campaign-summary-value" style={{ color }}>
                    {value}
                  </strong>
                  <button
                    type="button"
                    className="campaign-summary-link-hint"
                    onClick={() => {
                      const { path, state } = buildCampaignDetailNavigation(
                        routeAppId,
                        "organic",
                        {
                          backSource: "campaign",
                          dateFrom,
                          dateTo,
                        }
                      );
                      navigate(path, { state });
                    }}
                  >
                    View details →
                  </button>
                </div>
              );
            }

            return (
              <div key={key} className="campaign-summary-card">
                <span className="campaign-summary-label">{displayLabel}</span>
                <strong className="campaign-summary-value" style={{ color }}>
                  {value}
                </strong>
              </div>
            );
          })}
        </div>

        {/* Event metrics — one row per scope (Campaign / Organic / Total); each row lists every
            configured metric's Count + Amount tile together, instead of scattering metrics among
            the generic clicks/installs/signups tiles above. */}
        {(summary.metrics || []).length > 0 && (
          <div>
            <h3 className="campaign-event-metrics-title">Event metrics</h3>
            {EVENT_METRIC_SCOPE_ROWS.map((row) => (
              <div key={row.key} className="campaign-event-metrics-row">
                <div className="campaign-event-metrics-scope-label">{row.rowLabel}</div>
                <div className="campaign-summary-row">
                  {summary.metrics.map((m) => (
                    <React.Fragment key={`${row.key}-metric-${m.id ?? "default"}`}>
                      {m.show_count !== false && (
                        <div className="campaign-summary-card">
                          <span className="campaign-summary-label">{composeEventMetricLabel(m.label, "Count")}</span>
                          <strong className="campaign-summary-value" style={{ color: row.color }}>
                            {loading ? "—" : bigAmountFormat(m[row.countField] || 0)}
                          </strong>
                        </div>
                      )}
                      {m.show_amount !== false && (
                        <div className="campaign-summary-card">
                          <span className="campaign-summary-label">{m.label}</span>
                          <strong className="campaign-summary-value" style={{ color: row.color }}>
                            {loading ? "—" : bigAmountFormat(m[row.amountField] || 0, getAuthUserCurrency())}
                          </strong>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <CampaignPerformanceTable
          rows={report?.by_campaign || []}
          loading={loading}
          appRouteId={routeAppId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          eventMetricLabel={summary.event_metric_label}
          metrics={summary.metrics}
        />
      </div>
    </div>
  );
}
